import { API, graphqlOperation } from "aws-amplify";
import mixpanel from "mixpanel-browser";

import { mixpanel as mixpanelConsts } from "../constants";
import SendUnsubscribeRequest from "../graphql/mutations/SendUnsubscribeRequest";
import { paginateData } from "./paginateData";
import AcknowledgeShift from "../graphql/mutations/AcknowledgeShift";
import ApproveTimeOff from "../graphql/mutations/ApproveTimeOff";
import CreatePropertyEvent from "../graphql/mutations/CreatePropertyEvent";
import CreatePatrol from "../graphql/mutations/CreatePatrol";
import CreateShift from "../graphql/mutations/CreateShift";
import CreateOpenShift from "../graphql/mutations/CreateOpenShift";
import DeleteShift from "../graphql/mutations/DeleteShift";
import DenyTimeOff from "../graphql/mutations/DenyTimeOff";
import OpenInstance from "../graphql/mutations/OpenInstance";
import PublishShifts from "../graphql/mutations/PublishShifts";
import UpdateIncidentReport from "../graphql/mutations/UpdateIncidentReport";
import UpdateProperty from "../graphql/mutations/UpdateProperty";
import UpdateServiceProviderUser from "../graphql/mutations/UpdateServiceProviderUser";
import UpdateShift from "../graphql/mutations/UpdateShift";
import GetIncidentReport from "../graphql/queries/GetIncidentReport";
import GetPatrol from "../graphql/queries/GetPatrol";
import QueryIncidentReports from "../graphql/queries/QueryIncidentReports";
import QueryInstances from "../graphql/queries/QueryInstances";
import QueryPatrols from "../graphql/queries/QueryPatrols";
import QueryPendingIncidentReports from "../graphql/queries/QueryPendingIncidentReportsByProperty";
import QueryPropertyEventsByInstanceIdCreatedAtIndex from "../graphql/queries/QueryPropertyEventsByInstanceIdCreatedAtIndex";
import QueryPropertiesByServiceProvider from "../graphql/queries/QueryPropertiesByServiceProvider";
import QueryUsersByServiceProvider from "../graphql/queries/QueryServiceProviderUsers";
import QueryShifts from "../graphql/queries/QueryShifts";
import QueryShiftsByUser from "../graphql/queries/QueryShiftsByUser";
import QueryShiftTemplatesByServiceProviderIdIndex from "../graphql/queries/QueryShiftTemplatesByServiceProviderIdIndex";
import CreatePushToken from "../graphql/mutations/CreatePushToken";
import GetDirections from "../graphql/queries/GetDirections";
import CreateServiceProviderUser from "../graphql/mutations/CreateServiceProviderUser";
import GetFormattedAddress from "../graphql/queries/GetFormattedAddress";
import AdminCreateProperty from "../graphql/mutations/AdminCreateProperty";
import UpgradeProperty from "../graphql/mutations/UpgradeProperty";
import ResendSPTempPass from "../graphql/mutations/ResendSPTempPass";
import { appsyncError } from "./errors";
import QueryLocationUpdates from "../graphql/queries/QueryLocationUpdates";
import QueryDivisions from "../graphql/queries/QueryDivisions";
import QueryDivisionRelations from "../graphql/queries/QueryDivisionRelations";
import CreateDivision from "../graphql/mutations/CreateDivision";
import DeleteDivision from "../graphql/mutations/DeleteDivision";
import UpdateDivision from "../graphql/mutations/UpdateDivision";
import CreateDivisionRelation from "../graphql/mutations/CreateDivisionRelation";
import DeleteDivisionRelation from "../graphql/mutations/DeleteDivisionRelation";
import InitClient from "../graphql/mutations/InitClient";
import BatchWriteDivisionRelations from "../graphql/mutations/BatchWriteDivisionRelations";
import ShiftReminders from "../graphql/mutations/ShiftReminders";
import SendReport from "../graphql/mutations/SendReport";
import QueryPatrolsByServiceProviderId from "../graphql/queries/QueryPatrolsByServiceProviderId";
import QueryPatrolStartTimesByServiceProviderId from "../graphql/queries/QueryPatrolStartTimesByServiceProviderId";
import QueryIncidentReportsByServiceProviderId from "../graphql/queries/QueryIncidentReportsByServiceProviderId";
import QueryResponsesByServiceProviderId from "../graphql/queries/QueryResponsesByServiceProviderId";
import QueryPatrolsByPropertyIdStartTimeIndex from "../graphql/queries/QueryPatrolsByPropertyIdStartTimeIndex";
import QueryIncidentReportsByPropertyIdStartTimeIndex from "../graphql/queries/QueryIncidentReportsByPropertyIdStartTimeIndex";
import QueryInstancesByPropertyIdStartTimeIndex from "../graphql/queries/QueryInstancesByPropertyIdStartTimeIndex";
import UpdatePatrol from "../graphql/mutations/UpdatePatrol";
import GetServiceProvider from "../graphql/queries/GetServiceProvider";
import CreateShiftTemplate from "../graphql/mutations/CreateShiftTemplate";
import UpdateShiftTemplate from "../graphql/mutations/UpdateShiftTemplate";
import DeleteShiftTemplate from "../graphql/mutations/DeleteShiftTemplate";
import ApproveCoverage from "../graphql/mutations/ApproveCoverage";
import DenyCoverage from "../graphql/mutations/DenyCoverage";
import RequestResponse from "../graphql/mutations/RequestResponse";
import CloseResponse from "../graphql/mutations/CloseResponse";
import QueryPatrolsByServiceProviderIdUpdatedAtIndex from "../graphql/queries/QueryPatrolsByServiceProviderIdUpdatedAtIndex";
import DeleteServiceProviderUser from "../graphql/mutations/DeleteServiceProviderUser";
import RestoreServiceProviderUser from "../graphql/mutations/RestoreServiceProviderUser";
import QueryClients from "../graphql/queries/QueryClients";
import CreateClientWithEmail from "../graphql/mutations/CreateClientWithEmail";
import UpdateClient from "../graphql/mutations/UpdateClient";
import ArchiveClient from "../graphql/mutations/ArchiveClient";
import RestoreClient from "../graphql/mutations/RestoreClient";
import DeleteClient from "../graphql/mutations/DeleteClient";
import SetClientPassword from "../graphql/mutations/SetClientPassword";
import ApproveReports from "../graphql/mutations/ApproveReports";
import UnapproveReports from "../graphql/mutations/UnapproveReports";
import SendReportEmails from "../graphql/mutations/SendReportEmails";
import DeletePatrolMedia from "../graphql/mutations/DeletePatrolMedia";
import DeleteIncidentMedia from "../graphql/mutations/DeleteIncidentMedia";
import MarkAsProcessed from "../graphql/mutations/MarkAsProcessed";
import MarkAsNotProcessed from "../graphql/mutations/MarkAsNotProcessed";

// Queries
export const queryClients = async ({ from, serviceProviderId }) =>
  paginateData({
    operation: QueryClients,
    operationName: "queryClients",
    input: { from, serviceProviderId },
  });

export const getServiceProvider = async (serviceProviderId) => {
  try {
    const response = await API.graphql(
      graphqlOperation(GetServiceProvider, { serviceProviderId })
    );
    return response.data.getServiceProvider;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const getFormattedAddress = async (address) => {
  try {
    const response = await API.graphql(
      graphqlOperation(GetFormattedAddress, { address })
    );
    return response.data.getFormattedAddress;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const getProperties = ({
  serviceProviderId,
  updatedAt,
  includeDeleted = true,
}) =>
  paginateData({
    operation: QueryPropertiesByServiceProvider,
    operationName: "queryPropertiesByServiceProviderIdUpdatedAtIndex",
    input: { serviceProviderId, updatedAt, includeDeleted },
  });

export const getServiceProviderUsers = ({ serviceProviderId, from, to }) =>
  paginateData({
    operation: QueryUsersByServiceProvider,
    operationName: "queryServiceProviderUsersByServiceProviderIdUpdatedAtIndex",
    input: { serviceProviderId, from, to },
  });

export const getPropertyEventsForInstance = (instanceId) =>
  paginateData({
    operation: QueryPropertyEventsByInstanceIdCreatedAtIndex,
    operationName: "queryPropertyEventsByInstanceIdCreatedAtIndex",
    input: { instanceId },
  });

export const getDirections = async ({ origin, destination }) => {
  try {
    const response = await API.graphql(
      graphqlOperation(GetDirections, { origin, destination })
    );
    return response.data.getDirections;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const getPatrol = async (patrolId) => {
  try {
    const response = await API.graphql(
      graphqlOperation(GetPatrol, { patrolId })
    );
    return response.data.getPatrol;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const queryPatrols = ({ propertyId, from, to }) =>
  paginateData({
    operation: QueryPatrols,
    operationName: "queryPatrolsByPropertyIdStartTimeIndex",
    input: {
      propertyId,
      startTime: from,
      to,
    },
  });

export const queryPatrolsByServiceProviderId = ({
  serviceProviderId,
  from,
  to,
}) => {
  return paginateData({
    operation: QueryPatrolsByServiceProviderId,
    operationName: "queryPatrolsByServiceProviderIdStartTimeIndex",
    input: {
      serviceProviderId,
      from,
      to,
    },
  });
};

export const queryPatrolStartTimesByServiceProviderId = ({
  serviceProviderId,
  from,
  to,
}) =>
  paginateData({
    operation: QueryPatrolStartTimesByServiceProviderId,
    operationName: "queryPatrolsByServiceProviderIdStartTimeIndex",
    input: {
      serviceProviderId,
      from,
      to,
    },
  });

export const queryPatrolsByServiceProviderIdUpdatedAtIndex = ({
  serviceProviderId,
  from,
}) =>
  paginateData({
    operation: QueryPatrolsByServiceProviderIdUpdatedAtIndex,
    operationName: "queryPatrolsByServiceProviderIdUpdatedAtIndex",
    input: {
      serviceProviderId,
      from,
    },
  });

export const queryResponsesByServiceProviderId = ({
  serviceProviderId,
  from,
  to,
}) => {
  return paginateData({
    operation: QueryResponsesByServiceProviderId,
    operationName: "queryInstancesByServiceProviderIdCreatedAtIndex",
    input: {
      serviceProviderId,
      from,
      to,
    },
  });
};

export const queryInstances = ({ propertyId, from, to }) =>
  paginateData({
    operation: QueryInstances,
    operationName: "queryInstancesByPropertyIdStartTimeIndex",
    input: {
      propertyId,
      from,
      to,
    },
  });

export const queryIncidentReportsByServiceProviderId = ({
  serviceProviderId,
  from,
  to,
  includeDeleted = false,
}) => {
  return paginateData({
    operation: QueryIncidentReportsByServiceProviderId,
    operationName: "queryIncidentReportsByServiceProviderIdStartTimeIndex",
    input: {
      serviceProviderId,
      from,
      to,
      includeDeleted,
    },
  });
};

export const getIncidentReport = async ({ incidentId, propertyId }) => {
  try {
    const response = await API.graphql(
      graphqlOperation(GetIncidentReport, { incidentId, propertyId })
    );
    return response.data.getIncidentReport;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const queryIncidentReports = ({
  propertyId,
  from,
  to,
  includeDeleted = false,
}) =>
  paginateData({
    operation: QueryIncidentReports,
    operationName: "queryIncidentReportsByPropertyIdStartTimeIndex",
    input: { propertyId, from, to, includeDeleted },
  });

export const queryPendingIncidentReports = ({
  propertyId,
  from,
  to,
  includeDeleted = false,
}) =>
  paginateData({
    operation: QueryPendingIncidentReports,
    operationName: "queryPendingIncidentReportsByPropertyId",
    input: { propertyId, from, to, includeDeleted },
  });

export const queryLocationUpdates = ({ userId, from, to }) =>
  paginateData({
    operation: QueryLocationUpdates,
    operationName: "queryLocationUpdates",
    input: { userId, from, to },
  });

export const queryShifts = ({
  serviceProviderId,
  from,
  to,
  includeDeleted = false,
}) =>
  paginateData({
    operation: QueryShifts,
    operationName: "queryShiftsByServiceProviderIdStartTimeIndex",
    input: { serviceProviderId, from, to, includeDeleted },
  });

export const queryShiftsByUser = ({
  userId,
  from,
  to,
  includeDeleted = false,
}) =>
  paginateData({
    operation: QueryShiftsByUser,
    operationName: "queryShiftsByUserIdStartTimeIndex",
    input: { userId, from, to, includeDeleted },
  });

export const queryShiftTemplates = (serviceProviderId) =>
  paginateData({
    operation: QueryShiftTemplatesByServiceProviderIdIndex,
    operationName: "queryShiftTemplatesByServiceProviderIdIndex",
    input: { serviceProviderId },
  });

export const getDivisions = ({ serviceProviderId, from, to }) =>
  paginateData({
    operation: QueryDivisions,
    operationName: "queryDivisionsByServiceProviderIdUpdatedAtIndex",
    input: { serviceProviderId, from, to },
  });

export const getDivisionRelations = ({ serviceProviderId, from, to }) =>
  paginateData({
    operation: QueryDivisionRelations,
    operationName: "queryDivisionRelationsByServiceProviderIdUpdatedAtIndex",
    input: { serviceProviderId, from, to },
  });

// Mutations
export const unapproveReports = async ({ reportsToUnapprove }) => {
  try {
    const { data } = await API.graphql(
      graphqlOperation(UnapproveReports, { reportsToUnapprove })
    );
    return data.unapproveReports;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const approveReports = async ({ reportsToApprove }) => {
  try {
    const { data } = await API.graphql(
      graphqlOperation(ApproveReports, { reportsToApprove })
    );
    mixpanel.track(mixpanelConsts.ADMIN_APPROVE_REPORTS);
    return data.approveReports;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const archiveClient = async ({ id, serviceProviderId }) => {
  try {
    const { data } = await API.graphql(
      graphqlOperation(ArchiveClient, { id, serviceProviderId })
    );
    return data.archiveClient;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const restoreClient = async ({ id, serviceProviderId }) => {
  try {
    const { data } = await API.graphql(
      graphqlOperation(RestoreClient, { id, serviceProviderId })
    );
    return data.restoreClient;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const deleteClient = async ({ id, serviceProviderId }) => {
  try {
    const { data } = await API.graphql(
      graphqlOperation(DeleteClient, { id, serviceProviderId })
    );
    return data.deleteClient;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const setClientPassword = async ({
  id,
  password,
  serviceProviderId,
}) => {
  try {
    const { data } = await API.graphql(
      graphqlOperation(SetClientPassword, { id, password, serviceProviderId })
    );
    return data.setClientPassword;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const updateClient = async ({
  serviceProviderId,
  id,
  firstName,
  lastName,
  propertyIds,
}) => {
  try {
    const response = await API.graphql(
      graphqlOperation(UpdateClient, {
        serviceProviderId,
        id,
        firstName,
        lastName,
        propertyIds,
      })
    );
    return response.data.updateClient;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const createClientWithEmail = async ({
  serviceProviderId,
  email,
  firstName,
  lastName,
  propertyIds,
}) => {
  try {
    const response = await API.graphql(
      graphqlOperation(CreateClientWithEmail, {
        serviceProviderId,
        email,
        firstName,
        lastName,
        propertyIds,
      })
    );
    mixpanel.track(mixpanelConsts.ADMIN_CREATE_CLIENT);
    return response.data.createClientWithEmail;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const restoreServiceProviderUser = async ({
  userId,
  serviceProviderId,
}) => {
  try {
    const response = await API.graphql(
      graphqlOperation(RestoreServiceProviderUser, {
        userId,
        serviceProviderId,
      })
    );
    mixpanel.track(mixpanelConsts.ADMIN_RESTORE_USER);
    return response.data.restoreServiceProviderUser;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const deleteServiceProviderUser = async ({
  userId,
  serviceProviderId,
}) => {
  try {
    const response = await API.graphql(
      graphqlOperation(DeleteServiceProviderUser, { userId, serviceProviderId })
    );
    mixpanel.track(mixpanelConsts.ADMIN_DELETE_USER);
    return response.data.deleteServiceProviderUser;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const requestResponse = async ({
  propertyId,
  serviceProviderId,
  senderName,
  senderId,
}) => {
  try {
    const input = { propertyId, serviceProviderId, senderName, senderId };
    const response = await API.graphql(
      graphqlOperation(RequestResponse, input)
    );
    mixpanel.track(mixpanelConsts.LOCATION_REQUEST_RESPONSE);
    return response.data.requestResponse;
  } catch (e) {
    throw appsyncError(e);
  }
};

export const closeResponse = async (propertyId) => {
  try {
    mixpanel.track(mixpanelConsts.LOCATION_RESOLVE_RESPONSE);
    const response = await API.graphql(
      graphqlOperation(CloseResponse, { propertyId })
    );
    return response.data.closeResponse;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const denyCoverage = async ({ shiftId, deniedBy }) => {
  try {
    mixpanel.track(mixpanelConsts.SCHEDULING_DENY_COVERAGE);
    const response = await API.graphql(
      graphqlOperation(DenyCoverage, { shiftId, deniedBy })
    );
    return response.data.denyCoverage;
  } catch (e) {
    throw appsyncError(e);
  }
};

export const approveCoverage = async ({ shiftId, approvedBy, levels }) => {
  try {
    mixpanel.track(mixpanelConsts.SCHEDULING_APPROVE_COVERAGE);
    const response = await API.graphql(
      graphqlOperation(ApproveCoverage, { shiftId, approvedBy, levels })
    );
    return response.data.approveCoverage;
  } catch (e) {
    throw appsyncError(e);
  }
};

export const deleteShiftTemplate = async (templateId) => {
  try {
    mixpanel.track(mixpanelConsts.SCHEDULING_DELETE_TEMPATE);
    const response = await API.graphql(
      graphqlOperation(DeleteShiftTemplate, { templateId })
    );
    return response.data.deleteShiftTemplate;
  } catch (e) {
    throw appsyncError(e);
  }
};

export const updateShiftTemplate = async ({
  displayName,
  templateId,
  time,
  userId,
  propertyId,
  note,
  requiredLevels,
  timezone,
  postOrders,
}) => {
  try {
    mixpanel.track(mixpanelConsts.SCHEDULING_UPDATE_TEMPATE);
    const response = await API.graphql(
      graphqlOperation(UpdateShiftTemplate, {
        input: {
          displayName,
          templateId,
          time,
          userId,
          propertyId,
          note,
          requiredLevels,
          timezone,
          postOrders,
        },
      })
    );
    return response.data.updateShiftTemplate;
  } catch (e) {
    throw appsyncError(e);
  }
};

export const createShiftTemplate = async ({
  displayName,
  templateId,
  time,
  userId,
  propertyId,
  note,
  requiredLevels,
  serviceProviderId,
  timezone,
  postOrders,
}) => {
  try {
    mixpanel.track(mixpanelConsts.SCHEDULING_CREATE_TEMPATE);
    const response = await API.graphql(
      graphqlOperation(CreateShiftTemplate, {
        displayName,
        templateId,
        time,
        userId,
        propertyId,
        note,
        requiredLevels,
        serviceProviderId,
        timezone,
        postOrders,
      })
    );
    return response.data.createShiftTemplate;
  } catch (e) {
    throw appsyncError(e);
  }
};

export const updatePatrol = async ({ patrolId, notes, attachedMedia }) => {
  try {
    mixpanel.track(mixpanelConsts.PATROL_EDIT);
    const response = await API.graphql(
      graphqlOperation(UpdatePatrol, {
        input: { patrolId, notes, attachedMedia },
      })
    );
    return response.data.updatePatrol;
  } catch (e) {
    throw appsyncError(e);
  }
};

export const sendReport = async ({
  serviceProviderId,
  responseKeys,
  incidentIds,
  darIds,
}) => {
  try {
    const response = await API.graphql(
      graphqlOperation(SendReport, {
        serviceProviderId,
        responseKeys,
        incidentIds,
        darIds,
      })
    );
    return response.data.sendReport;
  } catch (e) {
    throw appsyncError(e);
  }
};

export const shiftReminders = async (shiftIds) => {
  try {
    mixpanel.track(mixpanelConsts.SCHEDULING_SEND_REMINDERS);
    const response = await API.graphql(
      graphqlOperation(ShiftReminders, { shiftIds })
    );
    return response.data.shiftReminders;
  } catch (e) {
    throw appsyncError(e);
  }
};

export const adminCreateProperty = async (input) => {
  try {
    mixpanel.track(mixpanelConsts.ADMIN_CREATE_PROPERTY);
    const response = await API.graphql(
      graphqlOperation(AdminCreateProperty, input)
    );
    return response.data.adminCreateProperty;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const resendSPTempPass = async (input) => {
  try {
    mixpanel.track(mixpanelConsts.ADMIN_RESEND_TEMPORARY_PASSWORD);
    const response = await API.graphql(
      graphqlOperation(ResendSPTempPass, input)
    );
    return response.data.resendSPTempPass;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const upgradeProperty = async (input) => {
  try {
    const response = await API.graphql(
      graphqlOperation(UpgradeProperty, input)
    );
    return response.data.upgradeProperty;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const createServiceProviderUser = async (input) => {
  try {
    mixpanel.track(mixpanelConsts.ADMIN_CREATE_USER);
    const response = await API.graphql(
      graphqlOperation(CreateServiceProviderUser, input)
    );
    return response.data.createServiceProviderUser;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const createPropertyEvent = async (input) => {
  try {
    mixpanel.track(mixpanelConsts.LOCATION_SEND_MESSAGE);
    const response = await API.graphql(
      graphqlOperation(CreatePropertyEvent, input)
    );
    return response.data.createPropertyEvent;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const createDar = async (input) => {
  try {
    mixpanel.track(mixpanelConsts.PATROL_CREATE);
    const response = await API.graphql(graphqlOperation(CreatePatrol, input));
    return response.data.createPatrol;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const createPushToken = async (input) => {
  try {
    const response = await API.graphql(
      graphqlOperation(CreatePushToken, input)
    );
    return response.data.createPushToken;
  } catch (error) {
    appsyncError(error);
  }
};

export const openInstance = async (input) => {
  try {
    const response = await API.graphql(graphqlOperation(OpenInstance, input));
    return response.data.openInstance;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const updateProperty = async (input) => {
  console.log("updateProperty:input", input);
  try {
    mixpanel.track(mixpanelConsts.LOCATION_UPDATE);
    const response = await API.graphql(
      graphqlOperation(UpdateProperty, { input })
    );
    console.log("updateProperty:success", response.data.updateProperty);
    return response.data.updateProperty;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const updateServiceProviderUser = async (input) => {
  try {
    const response = await API.graphql(
      graphqlOperation(UpdateServiceProviderUser, { input })
    );
    return response.data.updateServiceProviderUser;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const updateIncidentReport = async (input) => {
  try {
    const response = await API.graphql(
      graphqlOperation(UpdateIncidentReport, { input })
    );
    return response.data.updateIncidentReport;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const createShift = async ({
  divisionId,
  shiftId,
  createdBy,
  note,
  startTime,
  endTime,
  requiredLevels,
  serviceProviderId,
  userId,
  timezone,
  postOrders,
}) => {
  try {
    mixpanel.track(mixpanelConsts.SCHEDULING_CREATE_SHIFT);
    const response = await API.graphql(
      graphqlOperation(CreateShift, {
        divisionId,
        shiftId,
        createdBy,
        note,
        startTime,
        endTime,
        requiredLevels,
        serviceProviderId,
        userId,
        timezone,
        postOrders,
      })
    );
    return response.data.createShift;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const createOpenShift = async ({
  divisionId,
  shiftId,
  createdBy,
  note,
  startTime,
  endTime,
  requiredLevels,
  serviceProviderId,
  timezone,
  postOrders,
}) => {
  try {
    mixpanel.track(mixpanelConsts.SCHEDULING_CREATE_OPEN_SHIFT);
    const response = await API.graphql(
      graphqlOperation(CreateOpenShift, {
        divisionId,
        shiftId,
        createdBy,
        note,
        startTime,
        endTime,
        requiredLevels,
        serviceProviderId,
        timezone,
        postOrders,
      })
    );
    return response.data.createShift;
  } catch (error) {
    console.log(error);
    throw appsyncError(error);
  }
};

export const updateShift = async ({
  shiftId,
  updatedBy,
  userId,
  propertyId,
  note,
  startTime,
  endTime,
  serviceProviderId,
  timezone,
  postOrders,
}) => {
  try {
    mixpanel.track(mixpanelConsts.SCHEDULING_UPDATE_SHIFT);
    const response = await API.graphql(
      graphqlOperation(UpdateShift, {
        shiftId,
        updatedBy,
        userId,
        propertyId,
        note,
        startTime,
        endTime,
        serviceProviderId,
        timezone,
        postOrders,
      })
    );
    return response.data.updateShift;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const deleteShift = async ({
  shiftId,
  updatedBy,
  serviceProviderId,
}) => {
  try {
    mixpanel.track(mixpanelConsts.SCHEDULING_DELETE_SHIFT);
    const response = await API.graphql(
      graphqlOperation(DeleteShift, { shiftId, updatedBy, serviceProviderId })
    );
    return response.data.deleteShift;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const acknowledgeShift = async (shiftId) => {
  try {
    const response = await API.graphql(
      graphqlOperation(AcknowledgeShift, { shiftId })
    );
    return response.data.acknowledgeShift;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const publishShifts = async (shiftIds) => {
  try {
    mixpanel.track(mixpanelConsts.SCHEDULING_PUBLISH_SHIFTS);
    const response = await API.graphql(
      graphqlOperation(PublishShifts, { shiftIds })
    );
    return response.data.publishShifts;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const approveTimeOff = async ({ shiftId, approvedBy }) => {
  try {
    mixpanel.track(mixpanelConsts.SCHEDULING_APPROVE_TIME_OFF);
    const response = await API.graphql(
      graphqlOperation(ApproveTimeOff, { shiftId, approvedBy })
    );
    return response.data.approveTimeOff;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const denyTimeOff = async ({ shiftId, deniedBy }) => {
  try {
    mixpanel.track(mixpanelConsts.SCHEDULING_DENY_TIME_OFF);
    const response = await API.graphql(
      graphqlOperation(DenyTimeOff, { shiftId, deniedBy })
    );
    return response.data.denyTimeOff;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const initClient = async (identityId) => {
  try {
    await API.graphql(graphqlOperation(InitClient, { identityId }));
  } catch (error) {
    throw appsyncError(error);
  }
};

export const createDivision = async ({
  divisionId,
  serviceProviderId,
  displayName,
}) => {
  try {
    mixpanel.track(mixpanelConsts.DIVISION_CREATE);
    const response = await API.graphql(
      graphqlOperation(CreateDivision, {
        divisionId,
        serviceProviderId,
        displayName,
      })
    );
    return response.data.createDivision;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const deleteDivision = async (divisionId) => {
  try {
    mixpanel.track(mixpanelConsts.DIVISION_DELETE);
    const response = await API.graphql(
      graphqlOperation(DeleteDivision, { divisionId })
    );
    return response.data.deleteDivision;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const updateDivision = async (input) => {
  try {
    mixpanel.track(mixpanelConsts.DIVISION_UPDATE);
    const response = await API.graphql(
      graphqlOperation(UpdateDivision, { input })
    );
    return response.data.updateDivision;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const createDivisionRelation = async ({
  divisionId,
  serviceProviderId,
  relationalId,
  variant,
}) => {
  try {
    mixpanel.track(mixpanelConsts.DIVISION_RELATION_CREATE);
    const response = await API.graphql(
      graphqlOperation(CreateDivisionRelation, {
        divisionId,
        serviceProviderId,
        relationalId,
        variant,
      })
    );
    return response.data.createDivisionRelation;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const deleteDivisionRelation = async ({ divisionId, relationalId }) => {
  try {
    mixpanel.track(mixpanelConsts.DIVISION_RELATION_DELETE);
    const response = await API.graphql(
      graphqlOperation(DeleteDivisionRelation, { divisionId, relationalId })
    );
    return response.data.deleteDivisionRelation;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const batchWriteDivisionRelations = async (input) => {
  try {
    mixpanel.track(mixpanelConsts.DIVISION_RELATION_BATCH_WRITE);
    const response = await API.graphql(
      graphqlOperation(BatchWriteDivisionRelations, { input })
    );
    return response.data.batchWriteDivisionRelations;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const sendReportEmails = async (sendReportEmailsInput) => {
  try {
    const response = await API.graphql(
      graphqlOperation(SendReportEmails, { sendReportEmailsInput })
    );
    return response.data.sendReportEmails;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const deletePatrolMedia = async ({ mediaId, reportId }) => {
  try {
    const response = await API.graphql(
      graphqlOperation(DeletePatrolMedia, {
        mediaId,
        reportId,
      })
    );
    return response.data.deletePatrolMedia;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const deleteIncidentMedia = async ({ mediaId, reportId }) => {
  try {
    const response = await API.graphql(
      graphqlOperation(DeleteIncidentMedia, {
        mediaId,
        reportId,
      })
    );
    return response.data.deleteIncidentMedia;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const markAsProcessed = async ({
  responseKeys,
  dailyActivityReportIds,
  incidentReportIds,
}) => {
  try {
    const response = await API.graphql(
      graphqlOperation(MarkAsProcessed, {
        responseKeys,
        dailyActivityReportIds,
        incidentReportIds,
      })
    );
    return response.data.markAsProcessed;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const markAsNotProcessed = async ({
  responseKeys,
  dailyActivityReportIds,
  incidentReportIds,
}) => {
  try {
    const response = await API.graphql(
      graphqlOperation(MarkAsNotProcessed, {
        responseKeys,
        dailyActivityReportIds,
        incidentReportIds,
      })
    );
    return response.data.markAsNotProcessed;
  } catch (error) {
    throw appsyncError(error);
  }
};

export const sendUnsubscribeRequest = async ({ serviceProviderId, userId }) => {
  console.log("sendUnsubscribeRequest:input", serviceProviderId, userId);
  try {
    await API.graphql(
      graphqlOperation(SendUnsubscribeRequest, { serviceProviderId, userId })
    );
  } catch (error) {
    throw appsyncError(error);
  }
};

export const queryPatrolsByServiceProviderIdPaginated = async ({
  serviceProviderId,
  from,
  to,
  limit = 100,
  after = null
}) => {
  try {
    const response = await API.graphql(
      graphqlOperation(QueryPatrolsByServiceProviderId, {
        serviceProviderId,
        from,
        to,
        after
      })
    );
    
    // Check if response exists and has the expected structure
    if (!response?.data?.queryPatrolsByServiceProviderIdStartTimeIndex) {
      console.warn('Unexpected response structure:', response);
      return { items: [], nextToken: null };
    }
    
    return response.data.queryPatrolsByServiceProviderIdStartTimeIndex;
  } catch (error) {
    console.error('Error in queryPatrolsByServiceProviderIdPaginated:', error);
    throw appsyncError(error);
  }
};

export const queryIncidentReportsByServiceProviderIdPaginated = async ({
  serviceProviderId,
  from,
  to,
  limit = 100,
  after = null
}) => {
  try {
    const response = await API.graphql(
      graphqlOperation(QueryIncidentReportsByServiceProviderId, {
        serviceProviderId,
        from,
        to,
        after
      })
    );
    
    // Check if response exists and has the expected structure
    if (!response?.data?.queryIncidentReportsByServiceProviderIdStartTimeIndex) {
      console.warn('Unexpected response structure:', response);
      return { items: [], nextToken: null };
    }
    
    return response.data.queryIncidentReportsByServiceProviderIdStartTimeIndex;
  } catch (error) {
    console.error('Error in queryIncidentReportsByServiceProviderIdPaginated:', error);
    throw appsyncError(error);
  }
};

export const queryResponsesByServiceProviderIdPaginated = async ({
  serviceProviderId,
  from,
  to,
  limit = 100,
  after = null
}) => {
  try {
    const response = await API.graphql(
      graphqlOperation(QueryResponsesByServiceProviderId, {
        serviceProviderId,
        from,
        to,
        after
      })
    );
    
    // Check if response exists and has the expected structure
    if (!response?.data?.queryInstancesByServiceProviderIdCreatedAtIndex) {
      console.warn('Unexpected response structure:', response);
      return { items: [], nextToken: null };
    }
    
    return response.data.queryInstancesByServiceProviderIdCreatedAtIndex;
  } catch (error) {
    console.error('Error in queryResponsesByServiceProviderIdPaginated:', error);
    throw appsyncError(error);
  }
};

// Optimized queries using propertyId indexes
export const queryPatrolsByPropertyIdStartTimeIndex = async ({
  propertyId,
  startTime,
  to,
  after = null
}) => {
  try {
    const response = await API.graphql(
      graphqlOperation(QueryPatrolsByPropertyIdStartTimeIndex, {
        propertyId,
        startTime,
        to,
        after
      })
    );
    
    if (!response?.data?.queryPatrolsByPropertyIdStartTimeIndex) {
      console.warn('Unexpected response structure:', response);
      return { items: [], nextToken: null };
    }
    
    return response.data.queryPatrolsByPropertyIdStartTimeIndex;
  } catch (error) {
    console.error('Error in queryPatrolsByPropertyIdStartTimeIndex:', error);
    throw appsyncError(error);
  }
};

export const queryIncidentReportsByPropertyIdStartTimeIndex = async ({
  propertyId,
  from,
  to,
  after = null
}) => {
  try {
    const response = await API.graphql(
      graphqlOperation(QueryIncidentReportsByPropertyIdStartTimeIndex, {
        propertyId,
        from,
        to,
        after
      })
    );
    
    if (!response?.data?.queryIncidentReportsByPropertyIdStartTimeIndex) {
      console.warn('Unexpected response structure:', response);
      return { items: [], nextToken: null };
    }
    
    return response.data.queryIncidentReportsByPropertyIdStartTimeIndex;
  } catch (error) {
    console.error('Error in queryIncidentReportsByPropertyIdStartTimeIndex:', error);
    throw appsyncError(error);
  }
};

export const queryInstancesByPropertyIdStartTimeIndex = async ({
  propertyId,
  from,
  to,
  after = null
}) => {
  try {
    const response = await API.graphql(
      graphqlOperation(QueryInstancesByPropertyIdStartTimeIndex, {
        propertyId,
        from,
        to,
        after
      })
    );
    
    if (!response?.data?.queryInstancesByPropertyIdStartTimeIndex) {
      console.warn('Unexpected response structure:', response);
      return { items: [], nextToken: null };
    }
    
    return response.data.queryInstancesByPropertyIdStartTimeIndex;
  } catch (error) {
    console.error('Error in queryInstancesByPropertyIdStartTimeIndex:', error);
    throw appsyncError(error);
  }
};
